import { ReactElement, ReactNode, useEffect } from 'react';

import { NextPage } from 'next';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Config } from '@forgerock/javascript-sdk/src/index';
import { AppProps } from 'next/app';
import Router from 'next/router';

import { captureUtmParams, pushEvent } from '@/helpers/analytics';

export function reportWebVitals({ name, value, id }: { name: string; value: number; id: string }) {
  const mapping: Record<string, string> = { CLS: 'cls', LCP: 'lcp', INP: 'inp', TTFB: 'ttfb', FCP: 'fcp' };
  if (mapping[name]) {
    pushEvent('core_web_vital', { [mapping[name]]: Math.round(name === 'CLS' ? value * 1000 : value), metric_id: id });
  }
}

import AppError from '@/components/molecules/AppError/AppError';
import {
  DATADOG_APP_ID,
  DATADOG_ALLOWTRACINGURL,
  DATADOG_CLIENT_TOKEN,
  DATADOG_DEFAULTPRIVACYLEVEL,
  DATADOG_ENV,
  DATADOG_SERVICE,
  DATADOG_SITE,
  DATADOG_VERSION,
  SESSIONREPLAYSAMPLERATE,
  SESSIONSAMPLERATE,
  TRACKLONGTASKS,
  TRACKRESOURCES,
  TRACKUSERINTERACTIONS,
  FORWARDERRORSTOLOGS,
  DATADOG_PRODUCT,
} from '@/constants/datadog_constants';
import {
  AM_URL,
  APP_URL,
  JOURNEY_LOGIN_UAEPASS,
  REALM_PATH,
  SCOPE,
  TIME_OUT,
  WEB_OAUTH_CLIENT,
} from '@/constants/forgrock.constants';
import Providers from '@/providers';
import { WebPage } from '@/types/ContentFul.type';
import { LocalizationTypes } from '@/types/localization.type';

// Config Forgerock
Config.set({
  clientId: WEB_OAUTH_CLIENT,
  redirectUri: `${APP_URL}/authenticate`,
  scope: SCOPE,
  serverConfig: {
    baseUrl: `${AM_URL}`,
    timeout: TIME_OUT,
  },
  realmPath: REALM_PATH,
  tree: JOURNEY_LOGIN_UAEPASS,
});

// Add Datadog RUM and logs initialization
datadogRum.init({
  applicationId: DATADOG_APP_ID || '',
  clientToken: DATADOG_CLIENT_TOKEN || '',
  site: DATADOG_SITE || '',
  service: DATADOG_SERVICE || '',
  env: DATADOG_ENV || '',
  version: DATADOG_VERSION || '', // The version you release
  sessionSampleRate: SESSIONSAMPLERATE,
  sessionReplaySampleRate: SESSIONREPLAYSAMPLERATE,
  trackUserInteractions: TRACKUSERINTERACTIONS,
  trackResources: TRACKRESOURCES,
  trackLongTasks: TRACKLONGTASKS,
  allowedTracingUrls: [DATADOG_ALLOWTRACINGURL || ''],
  defaultPrivacyLevel: DATADOG_DEFAULTPRIVACYLEVEL,
});

datadogRum.setGlobalContext({
  product: DATADOG_PRODUCT,
});
// console.log(DATADOG_PRODUCT);
// Add Datadog RUM log initialization
datadogLogs.init({
  clientToken: DATADOG_CLIENT_TOKEN || '',
  site: DATADOG_SITE || '',
  service: DATADOG_SERVICE || '',
  env: DATADOG_ENV || '',
  version: DATADOG_VERSION || '', // The version you release
  forwardErrorsToLogs: FORWARDERRORSTOLOGS,
  sessionSampleRate: SESSIONSAMPLERATE,
});

// Console tracking for Datadog initialization
// console.log('[Datadog] RUM and Logs initialized successfully', {
//   env: DATADOG_ENV,
//   service: DATADOG_SERVICE,
//   version: DATADOG_VERSION,
// });

export type NextPageWithLayout<Props> = NextPage<Props> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout<Props> = AppProps & {
  Component: NextPageWithLayout<Props>;
  locale: LocalizationTypes;
};
const getComponentWithError = (
  Component: NextPageWithLayout<WebPage>,
  pageProps: WebPage
) => {
  const { error } = pageProps;

  if (error) {
    return (
      <AppError
        status={error.status}
        extraDetails={{
          error: error.title,
          componentStack: error.message,
        }}
        refresh
      />
    );
  }
  return <Component {...pageProps} />;
};

function MyApp({ Component, pageProps }: AppPropsWithLayout<WebPage>) {
  const getLayout = Component.getLayout ?? (page => page);

  useEffect(() => {
    captureUtmParams();

    const handleUnhandledError = (event: ErrorEvent) => {
      pushEvent('js_error', {
        error_message: event.message,
        error_type: 'UnhandledError',
        screen_name: window.location.pathname,
      });
    };
    window.addEventListener('error', handleUnhandledError);

    if (process.env.NODE_ENV !== 'production' && typeof window.gtag === 'function') {
      window.gtag('config', process.env.NEXT_PUBLIC_GA_ID, { debug_mode: true });
    }

    const handleRouteChange = (url: string) => {
      pushEvent('page_view', {
        page_path: url,
        page_title: document.title,
        screen_name: url.split('?')[0],
        language_code: document.documentElement.lang || 'en',
      });
    };

    Router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
      window.removeEventListener('error', handleUnhandledError);
    };
  }, []);

  return (
    <Providers>
      {getLayout(getComponentWithError(Component, pageProps))}
    </Providers>
  );
}

export default MyApp;
